/**
 * appointmentService.ts
 *
 * Single source of truth for creating appointments across ALL phases
 * (chat, call, video, physical). Extracted from ExtendedAgenda.tsx to keep
 * business logic out of UI components and to make the critical write sequence
 * easier to test, audit and maintain.
 *
 * Responsibility boundary:
 *  - Creates the calendarEvent document
 *  - Links it to the connection journey (linkActiveEvent)
 *  - Sends notifications and inbox items (best-effort, non-blocking)
 *  - Updates the planningLock on the connection
 *
 * NOTE: The calendarEvent write and linkActiveEvent are intentionally NOT in a
 * single Firestore transaction because calendarEvents and connections are in
 * different document paths, and cross-collection transactions in Firestore are
 * limited to 500 operations. linkActiveEvent already uses its own updateDoc.
 * A failed linkActiveEvent is caught and logged — the calendar event still
 * exists and will be visible in the AgendaPage. A future improvement is to use
 * a Cloud Function to apply eventual consistency here.
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { linkActiveEvent, setPlanningLock } from './connectionJourneyService';

export type AppointmentPhase = 'chat' | 'call' | 'video' | 'physical';

const PHASE_LABELS: Record<AppointmentPhase, string> = {
  chat: 'Chat',
  call: 'Bellen',
  video: 'Beeldbellen',
  physical: 'Fysieke afspraak',
};

export interface CreateAppointmentParams {
  /** ISO date strings for the appointment slot */
  start: Date;
  end: Date;
  /** UID of the user initiating the appointment */
  creatorUid: string;
  creatorDisplayName: string;
  /** UID of the other participant */
  otherUserId: string;
  otherUserName: string;
  /** Optional connection ID — used to link via connectionJourneyService */
  connectionId: string | null;
  /** Location data — only relevant for the physical phase */
  locationName: string | null;
  locationAddress: string | null;
  /** Human-readable date range label, e.g. "wo 25 feb 14:00 – 15:00" */
  dateRangeText: string;
  /** The connection phase this appointment belongs to. Defaults to 'physical'. */
  phase?: AppointmentPhase;
}

/** Minimal params needed to write inbox confirmation items for both users. */
export interface AppointmentInboxParams {
  creatorUid: string;
  creatorDisplayName: string;
  otherUserId: string;
  otherUserName: string;
  connectionId: string | null;
  eventId: string;
  start: string;   // ISO string
  end: string;     // ISO string
  dateRangeText: string;
  phase: AppointmentPhase;
  locationName?: string | null;
}

export interface CreateAppointmentResult {
  /** Firestore document ID of the created calendarEvent */
  eventId: string;
  /** Whether linkActiveEvent succeeded */
  linkedToConnection: boolean;
}

/**
 * Write appointment_confirmed inbox items for both participants.
 *
 * Call this from any flow that confirms an appointment — autoplan, manual slot,
 * proposal acceptance, counter-proposal acceptance — so every confirmation
 * always lands in both users' inboxes with consistent data.
 *
 * Best-effort: logs failures but never throws.
 */
export async function createAppointmentInboxItems(
  params: AppointmentInboxParams
): Promise<void> {
  const {
    creatorUid,
    creatorDisplayName,
    otherUserId,
    otherUserName,
    connectionId,
    eventId,
    start,
    end,
    dateRangeText,
    phase,
    locationName,
  } = params;

  const phaseLabel = PHASE_LABELS[phase];
  // Only include location for physical meetings
  const location = phase === 'physical' ? (locationName ?? null) : null;

  const writes = [
    // Inbox item for the other user (receiver / the one who didn't initiate)
    addDoc(collection(db, 'inboxItems'), {
      userId: otherUserId,
      type: 'appointment_confirmed',
      category: 'afspraak',
      phase: phaseLabel,
      title: `Afspraak bevestigd met ${creatorDisplayName || 'Connectie'}`,
      message: dateRangeText,
      subtitle: location ?? undefined,
      fromUserId: creatorUid,
      fromUserName: creatorDisplayName || 'Connectie',
      isRead: false,
      priority: 'high',
      createdAt: serverTimestamp(),
      data: {
        connectionId,
        eventId,
        start,
        end,
        location,
        reminderMinutesBefore: 1440,
      },
    }),
    // Inbox item for the creator (self-confirmation)
    addDoc(collection(db, 'inboxItems'), {
      userId: creatorUid,
      type: 'appointment_confirmed',
      category: 'afspraak',
      phase: phaseLabel,
      title: `Afspraak bevestigd met ${otherUserName}`,
      message: dateRangeText,
      subtitle: location ?? undefined,
      fromUserId: otherUserId,
      fromUserName: otherUserName,
      isRead: false,
      priority: 'high',
      createdAt: serverTimestamp(),
      data: {
        connectionId,
        eventId,
        start,
        end,
        location,
        reminderMinutesBefore: 1440,
      },
    }),
  ];

  const results = await Promise.allSettled(writes);
  results.forEach((result, idx) => {
    if (result.status === 'rejected') {
      const labels = ['inbox:otherUser', 'inbox:self'];
      console.warn(`[appointmentService] createAppointmentInboxItems ${labels[idx]} failed:`, result.reason);
    }
  });
}

/**
 * Create an appointment for two users.
 *
 * - Writes the calendarEvent (with location + 24h reminder).
 * - Links to the connection journey (best-effort).
 * - Sends notifications + inbox items in parallel (best-effort).
 * - Updates planningLock on the connection (best-effort).
 *
 * Throws only for the calendarEvent write — that is the critical path.
 * All subsequent writes are logged on failure but never throw.
 */
export async function createAppointment(
  params: CreateAppointmentParams
): Promise<CreateAppointmentResult> {
  const {
    start,
    end,
    creatorUid,
    creatorDisplayName,
    otherUserId,
    otherUserName,
    connectionId,
    locationName,
    locationAddress,
    dateRangeText,
    phase = 'physical',
  } = params;

  // Guard: validate required fields early so Firestore never receives garbage data.
  if (!creatorUid || typeof creatorUid !== 'string' || creatorUid.trim().length === 0) {
    throw new Error('[appointmentService] createAppointment: creatorUid is required');
  }
  if (!otherUserId || typeof otherUserId !== 'string' || otherUserId.trim().length === 0) {
    throw new Error('[appointmentService] createAppointment: otherUserId is required');
  }
  if (!(start instanceof Date) || isNaN(start.getTime())) {
    throw new Error('[appointmentService] createAppointment: start must be a valid Date');
  }
  if (!(end instanceof Date) || isNaN(end.getTime()) || end <= start) {
    throw new Error('[appointmentService] createAppointment: end must be a valid Date after start');
  }

  const title = `Afspraak met ${otherUserName}`;
  const reminderAt = new Date(start.getTime() - 24 * 60 * 60 * 1000);

  // ── Critical write: calendar event ───────────────────────────────────────
  const eventRef = await addDoc(collection(db, 'calendarEvents'), {
    title,
    description: locationName
      ? `Gepland via Autoplan · Locatie: ${locationName}`
      : 'Gepland via Autoplan',
    startTime: start,
    endTime: end,
    type: 'networking',
    source: 'free_for_networking',
    eventType: 'networking_filled',
    categoryColor: 'purple',
    participants: [creatorUid, otherUserId],
    // Object format: keyed by UID so AgendaPage can look up names by participant ID
    participantNames: { [creatorUid]: creatorDisplayName || 'Ik', [otherUserId]: otherUserName },
    status: 'scheduled',
    createdBy: creatorUid,
    createdAt: serverTimestamp(),
    color: '#C084FC',
    connectionId: connectionId ?? null,
    location: locationName,
    locationAddress: locationAddress,
    reminderEnabled: true,
    reminderMinutesBefore: 1440,
    reminderAt,
    reminderSent: false,
  });

  const eventId = eventRef.id;

  // ── Best-effort: link to connection journey ───────────────────────────────
  let linkedToConnection = false;
  if (connectionId) {
    try {
      await linkActiveEvent(connectionId, eventId, phase, 'scheduled');
      linkedToConnection = true;
    } catch (err) {
      console.warn('[appointmentService] linkActiveEvent failed (non-critical):', err);
    }
  }

  // ── Best-effort: persist selectedLocation on physicalMeeting ─────────────
  // PhysicalMeetingPhaseV2 reads physicalMeeting.selectedLocation to get the
  // meeting coordinates for GPS verification and Google Maps navigation. When
  // an appointment is scheduled via ExtendedAgenda (this service), that field
  // is never written — causing "Geen locatie geselecteerd" errors. We write a
  // minimal selectedLocation stub here so the phase UI can always resolve it.
  if (connectionId && phase === 'physical' && (locationName || locationAddress)) {
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        'physicalMeeting.selectedLocation': {
          locationId: eventId,
          location: {
            // lat/lng are unknown here (only address/name available from params).
            // PhysicalMeetingPhaseV2.resolveLocation() will geocode from locationAddress
            // on the first load and persist the coordinates back to Firestore.
            lat: null,
            lng: null,
            address: locationAddress ?? locationName ?? '',
            name: locationName ?? '',
          },
          selectedBy: 'system',
          selectedAt: serverTimestamp(),
          planningInProgress: false,
        },
        'physicalMeeting.status': 'scheduled',
      });
    } catch (err) {
      console.warn('[appointmentService] physicalMeeting.selectedLocation write failed (non-critical):', err);
    }
  }

  // ── Best-effort: notifications (parallel) ────────────────────────────────
  const locationSuffix = phase === 'physical' && locationName ? ` bij ${locationName}` : '';
  const notificationWrites = [
    addDoc(collection(db, 'notifications'), {
      userId: otherUserId,
      type: 'meeting_confirmed',
      title: '📅 Afspraak gepland',
      message: `${creatorDisplayName || 'Connectie'} heeft een afspraak ingepland op ${dateRangeText}${locationSuffix}.`,
      data: { connectionId, eventId, otherUserId: creatorUid, otherUserName: creatorDisplayName, start: start.toISOString(), end: end.toISOString() },
      read: false,
      createdAt: serverTimestamp(),
    }),
    addDoc(collection(db, 'notifications'), {
      userId: creatorUid,
      type: 'meeting_confirmed',
      title: '📅 Afspraak ingepland',
      message: `Je hebt een afspraak ingepland met ${otherUserName} op ${dateRangeText}${locationSuffix}.`,
      data: { connectionId, eventId, otherUserId, otherUserName, start: start.toISOString(), end: end.toISOString() },
      read: false,
      createdAt: serverTimestamp(),
    }),
  ];

  const notifResults = await Promise.allSettled(notificationWrites);
  notifResults.forEach((result, idx) => {
    if (result.status === 'rejected') {
      const labels = ['notification:otherUser', 'notification:self'];
      console.warn(`[appointmentService] ${labels[idx]} write failed (non-critical):`, result.reason);
    }
  });

  // ── Best-effort: inbox items via shared helper ────────────────────────────
  await createAppointmentInboxItems({
    creatorUid,
    creatorDisplayName,
    otherUserId,
    otherUserName,
    connectionId,
    eventId,
    start: start.toISOString(),
    end: end.toISOString(),
    dateRangeText,
    phase,
    locationName,
  });

  // ── Best-effort: update planningLock via service (consistent with rest of codebase) ──
  if (connectionId) {
    try {
      await setPlanningLock(connectionId, {
        status: 'scheduled',
        lockedByUid: creatorUid,
        lockedByUsername: otherUserName,
        lockReason: 'autoplan',
        expiresAt: new Date(Date.now() + 15 * 60 * 1000),
      });
    } catch (err) {
      console.warn('[appointmentService] planningLock update failed (non-critical):', err);
    }
  }

  // ── Best-effort: clear any outstanding planning_pending reminders ─────────
  // When planning completes, inbox items of type 'planning_pending' that
  // were created during the planning phase (e.g. "X is planning your meeting")
  // should be marked as resolved so they no longer appear as action items.
  if (connectionId) {
    try {
      // connectionId is stored under data.connectionId (nested), not as a top-level field.
      const pendingQuery = query(
        collection(db, 'inboxItems'),
        where('type', '==', 'planning_pending'),
        where('data.connectionId', '==', connectionId)
      );
      const pendingSnap = await getDocs(pendingQuery);
      if (!pendingSnap.empty) {
        await Promise.all(
          pendingSnap.docs.map(d =>
            updateDoc(doc(db, 'inboxItems', d.id), {
              isRead: true,
              resolvedAt: serverTimestamp(),
              resolvedReason: 'planning_completed',
            })
          )
        );
      }
    } catch (err) {
      console.warn('[appointmentService] planning_pending cleanup failed (non-critical):', err);
    }
  }

  // ── Best-effort: clear stale planningNotifications for this connection ────
  // Any unread planning_started / location_selected_planning notifications that
  // were written during the planning session must be marked as read now that the
  // appointment is confirmed. If they are left unread they will be picked up by
  // the ConnectieFlow planningNotifications onSnapshot listener the next time the
  // component mounts, causing the ExtendedAgenda to flash open unexpectedly.
  if (connectionId) {
    try {
      const planningNotifQuery = query(
        collection(db, 'planningNotifications'),
        where('connectionId', '==', connectionId),
        where('read', '==', false)
      );
      const planningNotifSnap = await getDocs(planningNotifQuery);
      if (!planningNotifSnap.empty) {
        await Promise.all(
          planningNotifSnap.docs.map(d =>
            updateDoc(doc(db, 'planningNotifications', d.id), {
              read: true,
              resolvedAt: serverTimestamp(),
              resolvedReason: 'appointment_confirmed',
            })
          )
        );
      }
    } catch (err) {
      console.warn('[appointmentService] planningNotifications cleanup failed (non-critical):', err);
    }
  }

  return { eventId, linkedToConnection };
}
